import { documentationClient } from 'shared/domain/documentation/documentationClient';
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { isFileExportAllowed } from 'services/fileService';
import { FormattedMessage } from 'intl';
import { LocalStorageManager } from 'services/storage/LocalStorageManager';
import type { ExportToDocumentationResultData } from 'shared/domain/issue/linkedDocumentation/operations';
import { exportedVersionSettingNotSavedKey } from 'intl/helpers/documentation';
import { CommonTranslationKeys, DocumentationTranslationKeys } from 'intl/keys';

import { ButtonText } from 'components/general';
import { DocumentationBrowserDialog } from 'components/documentation/DocumentationBrowser';
import { AddVersionDialog } from 'components/documentation/AddVersionDialog';
import { useExportToDocumentation } from './ExportToDocumentationContext';
import { useExportToDocumentationBrowser } from './useExportToDocumentationBrowser';
import {
  getDefaultVersionName,
  getExportedFileName,
  getNextVersionNumber,
  toExportedVersionOptions,
  type ExportedVersionChoice,
} from './model';
import { useIssueRouteParams } from 'hooks/navigation/useRouteParams';
import { openDirectoryForm } from 'presentation/directory/openDirectoryInput';
import { isNonEmptyArray } from 'shared/utils/array';
import { isErrorWithMessage } from 'shared/utils/errorHandling';
import { useDocumentationStore } from 'store/documentation';
import { ROOT_DIR_ID } from 'shared/constants/directory';
import {
  showErrorToastAndLog,
  showToast,
} from 'services/toaster';
import { TOASTER_TYPES } from 'components/core/Toaster/types';
import { useIssueDocumentationOperations, useIssueStore, type ExportAsVersion } from 'store/issue';
import { exportHintSx } from './styles';
import { useAutoEnterUploadedDirectory } from './useAutoEnterUploadedDirectory';
import { useCurrentIssue } from 'components/dataProviders/withIssue';
import {
  createLinkedDocumentationItem,
  isAlreadyLinked,
  LinkedDocumentationType,
} from 'shared/domain/issue/linkedDocumentation';

/** The dialog that adds the file as a version, while it is open. */
interface VersionDialogState {
  defaultMakeCurrent: boolean;
}

/** Tells which settings of an exported version could not be saved, while the export itself succeeded. */
function showUnsavedVersionSettings({ unsavedVersionSettings = [] }: ExportToDocumentationResultData): void {
  unsavedVersionSettings.forEach((setting) =>
    showToast({ message: { id: exportedVersionSettingNotSavedKey(setting) }, type: TOASTER_TYPES.FAILURE })
  );
}

export function ExportToDocumentationDialog(): ReactElement {
  const { filesToExport, isOpen, closeExportDialog } =
    useExportToDocumentation();
  const exportableFiles = useMemo(
    () => filesToExport.filter((f) => f._id && isFileExportAllowed(f)),
    [filesToExport]
  );
  const [exportedFile] = exportableFiles;
  const hasExportableFiles = isNonEmptyArray(exportableFiles);
  // A version is added from one file at a time: with several files a documentation cannot be picked.
  const canExportAsVersion = exportableFiles.length === 1;
  const browser = useExportToDocumentationBrowser(isOpen, canExportAsVersion);
  const targetDocumentation = canExportAsVersion
    ? browser.selectedDocumentation
    : undefined;
  // The version dialog while it is open, with the choice it starts from: the user's last one,
  // read from the storage once, when the dialog opens.
  const [versionDialog, setVersionDialog] = useState<VersionDialogState | undefined>(undefined);
  const closeVersionDialog = useCallback(() => setVersionDialog(undefined), []);
  // Set before the store's pending flag reaches the dialogs: a second click starts no second export.
  const isExportingRef = useRef(false);
  const { issueId } = useIssueRouteParams();
  const resyncAllDirectories = useDocumentationStore.use.resyncAllDirectories();
  const fetchDirectoryContent = useDocumentationStore.use.fetchDirectoryContent();
  const resyncIssue = useIssueStore.use.resyncCurrentIssue();
  const { exportToDocumentation, exportPending } =
    useIssueDocumentationOperations();
  const resetLinkedDocumentationPreview =
    useIssueStore.use.resetLinkedDocumentationPreview();
  const resetLinkedDocumentationDetails =
    useIssueStore.use.resetLinkedDocumentationDetails();
  const issue = useCurrentIssue();
  const setLinkedDocumentation = useIssueStore.use.setLinkedDocumentation();

  useAutoEnterUploadedDirectory({ isOpen, browser });

  const handleExport = useCallback(async (asVersion?: ExportAsVersion) => {
    if (!issueId || !hasExportableFiles || isExportingRef.current) return;
    isExportingRef.current = true;

    let linkedDocumentation = issue?.linkedDocumentation ?? [];
    let failedCount = 0;

    for (const file of exportableFiles) {
      try {
        const result = await exportToDocumentation({
          issueId,
          fileToExport: file,
          currentDirectoryId: browser.currentDirectoryId,
          selectedDirectoryId: browser.selectedDirectoryId,
          ...asVersion,
        });

        const alreadyPresent = isAlreadyLinked(linkedDocumentation, {
          type: LinkedDocumentationType.version,
          documentationId: result.documentationId,
          versionId: result.versionId,
        });

        if (!alreadyPresent) {
          const optimisticItem = createLinkedDocumentationItem({
            type: LinkedDocumentationType.version,
            documentationId: result.documentationId,
            versionId: result.versionId,
            sourceDocumentId: typeof file._id === 'string' ? file._id : undefined,
            nameCandidates: [
              result.documentationName,
              file.description,
              file.title,
              file.data?.name,
            ],
            descriptionCandidates: [file.description],
            imageRotationCandidates: [file.imageRotation],
          });

          linkedDocumentation = [...linkedDocumentation, optimisticItem];
        }

        showUnsavedVersionSettings(result);
        resetLinkedDocumentationPreview(String(result.versionId));
        resetLinkedDocumentationDetails(String(result.documentationId));
      } catch (err) {
        failedCount += 1;
        const message = isErrorWithMessage(err)
          ? String(err.message)
          : undefined;
        if (message && message !== 'Something went wrong') {
          showToast({ message, type: TOASTER_TYPES.FAILURE });
        } else {
          showErrorToastAndLog('Export to documentation failed', err);
        }
      }
    }

    try {
      if (failedCount < exportableFiles.length) {
        setLinkedDocumentation(issueId, linkedDocumentation);
        documentationClient.sync();
        resyncAllDirectories();
        const targetDirectoryId =
          browser.selectedDirectoryId ??
          browser.currentDirectoryId ??
          ROOT_DIR_ID;
        await fetchDirectoryContent(targetDirectoryId, {
          suppressLoadingIndicators: true,
        });
        resyncIssue({ force: true });
      }
    } finally {
      isExportingRef.current = false;
      closeVersionDialog();
      closeExportDialog();
    }
  }, [
    browser.currentDirectoryId,
    browser.selectedDirectoryId,
    fetchDirectoryContent,
    closeExportDialog,
    closeVersionDialog,
    exportableFiles,
    exportToDocumentation,
    hasExportableFiles,
    issue,
    issue?.linkedDocumentation,
    issueId,
    resyncAllDirectories,
    resyncIssue,
    setLinkedDocumentation,
    resetLinkedDocumentationDetails,
    resetLinkedDocumentationPreview,
  ]);

  const handleConfirm = useCallback(() => {
    if (targetDocumentation) {
      setVersionDialog({ defaultMakeCurrent: LocalStorageManager.getExportVersionMakeCurrent() });
      return;
    }
    handleExport();
  }, [handleExport, targetDocumentation]);

  const handleConfirmVersion = useCallback(
    (choice: ExportedVersionChoice) => {
      if (!targetDocumentation?._id || !exportedFile) return;

      LocalStorageManager.setExportVersionMakeCurrent(choice.makeCurrent);
      handleExport({
        targetDocumentationId: targetDocumentation._id,
        version: toExportedVersionOptions(choice, getDefaultVersionName(exportedFile)),
      });
    },
    [exportedFile, handleExport, targetDocumentation]
  );

  const handleNewDirectory = useCallback(() => {
    openDirectoryForm(undefined, {
      parentId: browser.currentDirectoryId || undefined,
    });
  }, [browser.currentDirectoryId]);

  if (!hasExportableFiles) return <></>;

  return (
    <>
      <DocumentationBrowserDialog
        isOpen={isOpen}
        onClose={closeExportDialog}
        titleFallbackId={CommonTranslationKeys.exportToDocumentation}
        searchDataQa='export-to-documentation-search'
        browser={browser}
        options={
          canExportAsVersion ? (
            <Typography
              variant='body2'
              color='text.secondary'
              sx={exportHintSx}
              data-qa='export-to-documentation-hint'
            >
              {targetDocumentation ? (
                <FormattedMessage id={DocumentationTranslationKeys.exportHintAsVersion} />
              ) : (
                <FormattedMessage id={DocumentationTranslationKeys.exportHintAsNew} />
              )}
            </Typography>
          ) : undefined
        }
        leftActions={
          <ButtonText onClick={handleNewDirectory}>
            <FormattedMessage id={DocumentationTranslationKeys.createDirectory} />
          </ButtonText>
        }
        confirmLabelId={CommonTranslationKeys.export}
        onConfirm={handleConfirm}
        confirmDisabled={
          exportPending ||
          browser.loading ||
          !issueId ||
          !hasExportableFiles ||
          Boolean(browser.loadError)
        }
        confirmLoading={exportPending || browser.loading}
      />
      {targetDocumentation && exportedFile && (
        <AddVersionDialog
          isOpen={Boolean(versionDialog)}
          documentationName={targetDocumentation.name}
          fileName={getExportedFileName(exportedFile)}
          versionNumber={getNextVersionNumber(targetDocumentation)}
          defaultName={getDefaultVersionName(exportedFile)}
          defaultMakeCurrent={versionDialog?.defaultMakeCurrent ?? true}
          loading={exportPending}
          onConfirm={handleConfirmVersion}
          onCancel={closeVersionDialog}
        />
      )}
    </>
  );
}
